import {
    ColDef,
    ColGroupDef,
    ICellRendererParams,
    ValueFormatterParams,
    ValueGetterParams,
} from 'ag-grid-community';
import { DateString } from 'routes/HumanResourcesModule/business/types';
import {
    formatTitleToExport,
    LIGHTGRAY_HEADER_CLASS,
} from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/gridHelpers';
import { DEFAULT_AMOUNT_COLUMN } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/defaultColDefs';

import {
    SIMULATED_AMOUNT,
    HISTORIC_AMOUNT,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import { getDateColumnLabel } from 'routes/HumanResourcesModule/helpers/dataHelpers';
import { t } from 'i18next';
import { currencyFormatter } from 'helpers/formatHelper';
import { SimulationDateString } from './types';

type DateColumnGetter = (
    date: DateString,
    startDate: SimulationDateString,
) => ColDef;

export const isRowGrouped = (params: ICellRendererParams): boolean =>
    params.node.rowGroupColumn !== null &&
    params.node.rowGroupColumn !== undefined;

export const isRowPinned = (
    params: ICellRendererParams | ValueGetterParams,
): boolean =>
    params?.node?.rowPinned !== null && params?.node?.rowPinned !== undefined;

export const isFooterRow = (params: ICellRendererParams): boolean =>
    Boolean(params.node.footer);

const groupedColCellRendererSelector = (params: ICellRendererParams) =>
    params.node?.rowPinned === 'bottom'
        ? { component: 'bottomPinnedRowRenderer' }
        : { component: undefined };

// Base column configuration used for both simulated and realized columns
const BASE_COLUMN: ColDef = {
    ...DEFAULT_AMOUNT_COLUMN,
    cellRendererSelector: groupedColCellRendererSelector,
    valueFormatter: (params: ValueFormatterParams) =>
        currencyFormatter(params.value ?? 0), // to fill empty cell with 0
};

// Function for creating a simulated column
export const createLoadedSimulatedColumn = (date: DateString) => ({
    ...BASE_COLUMN,
    headerName: t(`humanResources.simulation.loadedSimulated`),
    field: `${SIMULATED_AMOUNT}..${date}`,
});
export const createBrutSimulatedColumn = (date: DateString) => ({
    ...BASE_COLUMN,
    headerName: t(`humanResources.simulation.brutSimulated`),
    field: `${SIMULATED_AMOUNT}..${date}`,
});
export const createSimulatedColumn = (date: DateString) => ({
    ...BASE_COLUMN,
    headerName: t('humanResources.simulation.simulated'),
    field: `${SIMULATED_AMOUNT}..${date}`,
});

// Function to create a realized column
export const createRealizedColumn = (date: DateString) => ({
    ...BASE_COLUMN,
    headerName: t('humanResources.simulation.realized'),
    field: `${HISTORIC_AMOUNT}..${date}`,
    cellStyle: {
        color: 'lightgray',
    },
    headerClass: LIGHTGRAY_HEADER_CLASS,
});

// TODO: uncomment once ETP is simulated
// Function for creating an ETP column
// export const createEtpColumn =
//     (isPastColumn: boolean) => (date: DateString, t: TranslationT) => ({
//         ...NUMBER_FILTER,
//         headerName: t('humanResources.etp'),
//         field: `${ETP}..${date}`,
//         hide: true,
//         cellStyle: isPastColumn
//             ? {
//                   color: 'lightgray',
//               }
//             : undefined,
//         aggFunc: 'sum',
//         headerClass: isPastColumn ? LIGHTGRAY_HEADER_CLASS : undefined,
//         valueFormatter: floatFormatter,
//         cellRendererSelector: groupedColCellRendererSelector,
//     });

/**
 * Adjusts the starting month for a date range based on the provided start date.
 * If the start date is in January, February, or March, it adjusts the starting month
 * to include months from the previous year: October for January, November for February,
 * and December for March. If the start date is after March, it returns an empty list.
 *
 * @param {SimulationDateString} startDate - The start date of the period in YYYY-MM-DD format.
 * @returns {DateString[]} An array of date strings in YYYYMMDD format, representing adjusted start months.
 */
function adjustFirstMonths(startDate: SimulationDateString): DateString[] {
    const startDateObj = new Date(startDate);
    const monthList: DateString[] = [];
    const startMonth = startDateObj.getMonth();
    const startYear = startDateObj.getFullYear();

    if (![0, 1, 2].includes(startMonth)) return [];

    const monthToAdd = (((startMonth - 3) % 12) + 12) % 12;

    for (let month = monthToAdd; month < 12; month += 1) {
        const monthStr = (month + 1).toString().padStart(2, '0');
        monthList.push(`${startYear - 1}${monthStr}01` as DateString);
    }

    return monthList;
}

/**
 * Generates a list of months in YYYYMMDD format between two dates.
 * This function includes an option to adjust for full years or to respect the exact
 * start and end months within the start and end years.
 *
 * @param {SimulationDateString} startDate - The start date of the period.
 * @param {SimulationDateString} endDate - The end date of the period.
 * @param {boolean} [fullYears=true] - Whether to include full years regardless of start and end dates.
 * @returns {DateString[]} An array of date strings in YYYYMMDD format.
 */
export function getMonthList(
    startDate: SimulationDateString,
    endDate: SimulationDateString,
    fullYears: boolean = true,
): DateString[] {
    const startYear = new Date(startDate).getFullYear();
    const endYear = new Date(endDate).getFullYear();
    const monthList: DateString[] = [];

    for (let year = startYear; year <= endYear; year += 1) {
        let startMonth = 0;
        let endMonth = 11;

        // Adjust the start and end months for partial years if fullYears is false
        if (!fullYears) {
            if (year === startYear) {
                startMonth = new Date(startDate).getMonth();
            }
            if (year === endYear) {
                endMonth = new Date(endDate).getMonth();
            }
        }

        // Iterate over each month within the start and end year bounds
        for (let month = startMonth; month <= endMonth; month += 1) {
            const monthStr = (month + 1).toString().padStart(2, '0');
            const dayStr = '01';
            monthList.push(`${year}${monthStr}${dayStr}` as DateString);
        }
    }

    return fullYears
        ? [...adjustFirstMonths(startDate), ...monthList]
        : monthList;
}

/**
 * Function to generate a set of columns for each date between a simulation start and end date (inclusive).
 * Each column group includes a realized column and/or a simulated column depending on the date relative to the current date and simulation start date.
 *
 * @param {Object} params - The function parameters
 * @param {SimulationDateString} params.simulationStart - The start date of the simulation in yyyy-MM-dd format
 * @param {SimulationDateString} params.simulationEnd - The end date of the simulation in yyyy-MM-dd format
 * @param {DateColumnGetter[]} params.betweenCurrentAndSimulatedDateColumnGetters - Column getters used to get columns between current and simulation dates
 * @param {DateColumnGetter[]} params.pastDateColumnGetters - Column getters used to get columns before current date
 * @param {DateColumnGetter[]} params.simulatedDateColumnGetters - Column getters used to get columns after simulation start date
 * @returns {ColGroupDef[]} - An array of column group definitions for each month in the date range
 */
export const getAmountColumns = ({
    simulationStart,
    simulationEnd,
    betweenCurrentAndSimulatedDateColumnGetters,
    pastDateColumnGetters,
    simulatedDateColumnGetters,
    dateColumns,
    isMonthly = true,
}: {
    simulationStart: SimulationDateString;
    simulationEnd: SimulationDateString;
    betweenCurrentAndSimulatedDateColumnGetters: DateColumnGetter[];
    pastDateColumnGetters: DateColumnGetter[];
    simulatedDateColumnGetters: DateColumnGetter[];
    dateColumns?: DateString[]; // should only be defined when called in result
    isMonthly?: boolean;
}) => {
    // Get an array of all months in the simulation date range

    const dates = dateColumns ?? getMonthList(simulationStart, simulationEnd);

    const currentDate = new Date();

    // Convert the simulation start date to a Date object
    const simulationStartDate = new Date(simulationStart);

    // Map each date in the dateColumns array to a column group definition
    return dates.map((date) => {
        // Parse the year and month from the date string
        const dateYear = parseInt(date.slice(0, 4), 10);
        const dateMonth = parseInt(date.slice(4, 6), 10) - 1; // months are 0-indexed in JavaScript

        // Determine whether the date is in the past, before the simulation start date, or between the current date and the simulation start date
        // for the ongoing month, only the simulated column should be displayed, since the realized column is only available for month that have passed, hence only the month is compared
        const isPastDate = isMonthly
            ? new Date(dateYear, dateMonth) <
              new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
            : dateYear < currentDate.getFullYear();
        const isCurrentYear = dateYear === currentDate.getFullYear();

        // We consider the whole month of the simulation start date as part of the simulation.
        const isBeforeSimulationDate =
            new Date(dateYear, dateMonth) <
            new Date(
                simulationStartDate.getFullYear(),
                simulationStartDate.getMonth(),
            );
        const isBetweenCurrentAndSimulationDates = isMonthly
            ? !isBeforeSimulationDate && isPastDate
            : isCurrentYear;

        // Start building the column group definition for this date
        const finalColumn: ColGroupDef = {
            headerName: getDateColumnLabel(date, isMonthly),
            headerClass:
                isBeforeSimulationDate && isPastDate
                    ? LIGHTGRAY_HEADER_CLASS
                    : '',
            children: [],
        };

        // Based on the conditions above, determine which columns (realized, simulated, or both) should be included in this column group
        if (isBetweenCurrentAndSimulationDates) {
            finalColumn.children =
                betweenCurrentAndSimulatedDateColumnGetters.map((colGetter) =>
                    colGetter(date, simulationStart),
                );
        } else if (isPastDate) {
            finalColumn.children = pastDateColumnGetters.map((colGetter) =>
                colGetter(date, simulationStart),
            );
        } else {
            finalColumn.children = simulatedDateColumnGetters.map((colGetter) =>
                colGetter(date, simulationStart),
            );
        }
        // Return the final column group definition for this date
        return finalColumn;
    });
};
export const getCsvExportFilename = (exportFileName: string) => {
    return `${formatTitleToExport(exportFileName)}.csv`;
};
export const getExcelExportFilename = (exportFileName: string) => {
    return `${formatTitleToExport(exportFileName)}.xlsx`;
};
